import {useState, useEffect, useRef} from 'react'
import {useCustomQuery, useCustomMutation} from '@salesforce/commerce-sdk-react'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import mockData from '../../../components/pdp-reviews/mockData';
import useIsMobile from './use-is-mobile';
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const loadMockedData = false;


/**
 * Hook to fetch address suggestions based on address and postal code.
 */
const useBazarVoice = ({pid, page = 0, sort = 'SubmissionTime:desc'}) => {
    const {app: appConfig} = getConfig()
    const isMobile = useIsMobile();
    const [isLoading, setIsLoading] = useState(true)
    const [reviewsError, setReviewsError] = useState(null)
    const [limit, setLimit] = useState(null);

    useEffect(() => {
        setLimit(isMobile ? 8 : 10);
    }, [isMobile]);

    const {data: reviews} = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: {
                    c_pid: pid,
                    c_brandId: 'garage',
                    c_page: page,
                    c_sort: sort || 'SubmissionTime:desc',
                    ...(limit !== null && { c_limit: limit })
                },
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: '/bv-review',
                    apiName: 'bazaarvoice'
                }
            },
            rawResponse: false
        },
        {
            enabled: appConfig?.BV_ENABLE_RR_C2022 == 'true' && limit !== null,
            onSuccess: (result) => {
                setIsLoading(false)
                return result
            },
            onError: () => {
                setIsLoading(false)
                setReviewsError('Error fetching reviews');
            }
        }
    )

    return {
        isLoading: loadMockedData ? false : isLoading,
        reviews: loadMockedData ? mockData.bvReviewsAPIResponse : reviews,
        reviewsError
    }
}

const useReviewForm = ({pid}) => {
    const [isLoading, setIsLoading] = useState(true)

    const {refetch} = useCustomQuery({
        options: {
            method: 'GET',
            parameters: {
                c_pid: pid,
                c_brandId: 'garage'
            },
            customApiPathParameters: {
                apiVersion: 'v1',
                endpointPath: '/bv-reviewform',
                apiName: 'bazaarvoice'
            }
        },
        rawResponse: false
    },
    {
        enabled: false
    })

    const getReviewForm = async ({pid, shouldFetch = true}) => {
        if (!shouldFetch) return
        setIsLoading(true)

        try {
            return await refetch({
                options: {
                    parameters: {
                        c_pid: pid
                    }
                }
            })
        } catch (error) {
            logger.error('Error get review form', {
                namespace: 'bazaarvoice',
                additionalProperties: {error: error.message, stack: error.stack}
            })
        } finally {
            setIsLoading(false)
        }
    }

    return {
        isLoading,
        getReviewForm
    }
}

const review = () => {
    const [selectedId, setSelectedId] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const [terms, setTerms] = useState(false)
    const [rating, setRating] = useState('')
    const [title, setTitle] = useState('')
    const [userReview, setUserReview] = useState('')
    const [username, setUsername] = useState('')
    const [hdif, setHDIF] = useState('')
    const [email, setEmail] = useState('')
    const [height, setHeight] = useState('')
    const [weight, setWeight] = useState('')
    const [size, setSize] = useState('')
    const [photos, setPhotos] = useState([])

    const mutation = useCustomMutation({
        options: {
            method: 'POST',
            body: JSON.stringify({
                termsAndConditions: terms,
                rating: rating,
                title: title,
                review: userReview,
                username: username,
                howDoesItFit: hdif,
                userEmail: email,
                height: height,
                weight: weight,
                size: size,
                photoUrls: photos
            }),
            parameters: {
                c_pid: selectedId,
                c_brandId: 'garage'
            },
            customApiPathParameters: {
                apiVersion: 'v1',
                endpointPath: '/bv-submitreview',
                apiName: 'bazaarvoice'
            }
        }
    })

    const submitReview = async ({pid, formData, shouldFetch = true}) => {
        if (!shouldFetch) return
        setSelectedId(pid)
        let data

        let form = JSON.parse(formData)

        setTerms(form.agreedtotermsandconditions)
        setRating(parseInt(form.rating))
        setTitle(form.title)
        setUserReview(form.reviewtext)
        setUsername(form.usernickname)
        setHDIF(parseInt(form.rating_HowDoesItFit))
        setEmail(form.useremail)
        setHeight(form.contextdatavalue_height)
        setWeight(form.contextdatavalue_weight)
        setSize(form.additionalfield_sizeOrdered)

        let photos = []

        if (form.photourl_1) {
            photos.push(form.photourl_1)
        }

        if (form.photourl_2) {
            photos.push(form.photourl_2)
        }

        if (form.photourl_3) {
            photos.push(form.photourl_3)
        }

        if (form.photourl_4) {
            photos.push(form.photourl_4)
        }

        if (form.photourl_5) {
            photos.push(form.photourl_5)
        }

        if (form.photourl_6) {
            photos.push(form.photourl_6)
        }

        setPhotos(photos)

        setIsLoading(true)
        try {
            data = await mutation.mutateAsync({
                method: 'POST',
                options: {
                    parameters: {
                        c_pid: selectedId,
                        c_brandId: 'garage'
                    },
                    body: JSON.stringify({
                        termsAndConditions: terms,
                        rating: rating,
                        title: title,
                        review: userReview,
                        username: username,
                        howDoesItFit: hdif,
                        userEmail: email,
                        height: height,
                        weight: weight,
                        size: size,
                        photoUrls: photos
                    })
                }
            })
        } catch (error) {
            logger.error('Error submitting review', {
                namespace: 'bazaarvoice',
                additionalProperties: {error: error.message, stack: error.stack}
            })
        } finally {
            setIsLoading(false)
        }
        return data
    }

    return {
        isLoading,
        submitReview
    }
}

const reviewFeedback = () => {
    const [selectedFeedback, setSelectedFeedback] = useState('')
    const [selectedId, setSelectedId] = useState('');
    const [isLoading, setIsLoading] = useState(false)

    const mutation = useCustomMutation({
        options: {
            method: 'POST',
            parameters: {
                c_feedback: selectedFeedback,
                c_contentId: selectedId,
                c_brandId: 'garage'
            },
            customApiPathParameters: {
                apiVersion: 'v1',
                endpointPath: '/bv-submitfeedback',
                apiName: 'bazaarvoice'
            }
        }
    })

    const submitFeedback = async ({Id, feedback, shouldFetch = true}) => {
        if (!shouldFetch) return
        setSelectedFeedback(feedback)
        setSelectedId(Id);
        let data

        setIsLoading(true)
        try {
            data = await mutation.mutateAsync({
                method: 'POST',
                options: {
                    parameters: {
                        c_feedback: selectedFeedback,
                        c_contentId: selectedId,
                        c_brandId: 'garage'
                    }
                }
            })
        } catch (error) {
            logger.error('Error submitted review feedback', {
                namespace: 'bazaarvoice',
                additionalProperties: {error: error.message, stack: error.stack}
            })
        } finally {
            setIsLoading(false)
        }
        return data
    }

    return {
        isLoading,
        submitFeedback
    }
}

export {useBazarVoice, review, reviewFeedback, useReviewForm}
